import {
	routes,
	type EventReviews,
	type SubmitReviewsRequest,
	type UserReviewsResponse,
} from '@spot/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSession } from '../auth/session-gate';
import { profileQueryKey } from '../profile/use-profile';

const eventReviewsKey = (eventId: string) => ['events', eventId, 'reviews', 'mine'] as const;

export function userReviewsQueryKey(userId: string) {
	return ['users', userId, 'reviews'] as const;
}

/** S17 sheet state: who I can rate on this event and what I already wrote. */
export function useEventReviews(eventId: string) {
	const { api } = useSession();
	return useQuery<EventReviews>({
		queryKey: eventReviewsKey(eventId),
		queryFn: () => api.call(routes.myEventReviews, { params: { id: eventId } }),
	});
}

export function useSubmitReviews(eventId: string) {
	const { api } = useSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (body: SubmitReviewsRequest) =>
			api.call(routes.submitReviews, { params: { id: eventId }, body }),
		onSuccess: (state) => {
			queryClient.setQueryData(eventReviewsKey(eventId), state);
			// The rated people's profiles (rating, review list) changed.
			for (const target of state.targets) {
				void queryClient.invalidateQueries({ queryKey: profileQueryKey(target.user.id) });
			}
		},
	});
}

/** Profile «Відгуки» (S12/S13): newest first; empty until 3 reviews exist (D13). */
export function useUserReviews(userId: string) {
	const { api } = useSession();
	return useInfiniteQuery({
		queryKey: userReviewsQueryKey(userId),
		queryFn: ({ pageParam }) =>
			api.call(routes.userReviews, {
				params: { id: userId },
				query: pageParam === undefined ? {} : { cursor: pageParam },
			}),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (page: UserReviewsResponse) => page.nextCursor ?? undefined,
		staleTime: 30_000,
	});
}
